import React from "react";
import { useNavigate } from "react-router-dom";
import { FileCheck, Zap, Sparkles, Crown } from "lucide-react";
import Header from "./Header";

const LandingPage = ({ onSelectEngine, onLogin, onLogout }) => {
  const navigate = useNavigate();

  const handleEngineSelect = (engine) => {
    onSelectEngine(engine);
  };

  const handleLogout = () => {
    // App.jsx의 handleLogout 호출
    if (onLogout) {
      onLogout();
    } else {
      // fallback: onLogout이 없으면 새로고침
      window.location.reload();
    }
  };

  const handleSubscription = () => {
    navigate('/subscription');
  };

  return (
    <div
      className="h-screen transition-colors duration-300 overflow-hidden"
      style={{ backgroundColor: "hsl(var(--bg-100))" }}
    >
      {/* Header Component */}
      <Header
        onLogout={handleLogout}
        onAdminLogin={() => onLogin("admin")}
        onHome={() => window.location.reload()}
        onSubscription={handleSubscription}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div
          className="min-h-screen py-12 px-4"
          style={{
            background:
              "linear-gradient(135deg, hsl(var(--bg-100)) 0%, hsl(var(--bg-200)) 100%)",
          }}
        >
          <div className="container max-w-6xl mx-auto">
            {/* Main Header */}
            <div className="main-header text-center mb-12">
              <h1 className="app-title text-5xl font-bold mb-4">
                <span
                  className="bg-gradient-to-r bg-clip-text text-transparent"
                  style={{
                    backgroundImage:
                      "linear-gradient(135deg, hsl(var(--accent-main-000)), hsl(var(--accent-secondary-000)))",
                  }}
                >
                  AI Writer 서비스
                </span>
              </h1>
              <p className="app-subtitle text-xl text-text-300">
                AI 기반 뉴스 콘텐츠 작성 및 분석 솔루션
              </p>
            </div>

            {/* Product Selection */}
            <div className="main-content">
              <div className="product-selection">
                <h2 className="selection-title text-2xl font-semibold text-center mb-8 text-text-100">
                  뉴스 콘텐츠 제작을 위한 스마트한 AI 솔루션 선택
                </h2>

                <div className="product-cards grid md:grid-cols-3 gap-6 mb-8">
                  {/* Daily News Analysis Card */}
                  <div
                    className="product-card rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2 p-6 border-2 border-transparent hover:border-blue-500"
                    style={{ backgroundColor: "hsl(var(--bg-000))" }}
                    onClick={() => handleEngineSelect("daily-news-analysis")}
                  >
                    <div className="card-header flex justify-between items-start mb-4">
                      <div className="flex items-center gap-3">
                        <FileCheck className="w-8 h-8 text-blue-500" />
                        <div className="card-title text-2xl font-bold text-text-100">
                          Daily News Analysis
                        </div>
                      </div>
                      <div
                        className="card-badge px-3 py-1 rounded-full text-sm font-medium"
                        style={{
                          backgroundColor: "rgba(59, 130, 246, 0.1)",
                          color: "rgb(59, 130, 246)",
                        }}
                      >
                        일일 뉴스 분석
                      </div>
                    </div>

                    <div className="card-description text-text-300 mb-4">
                      매일 발생하는 뉴스를 빠르고 정확하게 분석하는 AI 서비스
                    </div>

                    <ul className="card-features space-y-2 text-sm">
                      <li className="flex items-start">
                        <span className="text-blue-500 mr-2">✓</span>
                        <span className="text-text-200">
                          실시간 뉴스 트렌드 분석
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-500 mr-2">✓</span>
                        <span className="text-text-200">
                          핵심 키워드 추출
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-500 mr-2">✓</span>
                        <span className="text-text-200">
                          관련 이슈 연결 분석
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-500 mr-2">✓</span>
                        <span className="text-text-200">
                          빠른 요약 리포트 생성
                        </span>
                      </li>
                    </ul>

                    <div className="mt-6 text-center">
                      <button
                        className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
                        onClick={() => handleEngineSelect("daily-news-analysis")}
                      >
                        시작하기
                      </button>
                    </div>
                  </div>

                  {/* Press Release Writing Card */}
                  <div
                    className="product-card rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2 p-6 border-2 border-transparent hover:border-accent-main-000"
                    style={{ backgroundColor: "hsl(var(--bg-000))" }}
                    onClick={() => handleEngineSelect("press-release-writing")}
                  >
                    <div className="card-header flex justify-between items-start mb-4">
                      <div className="flex items-center gap-3">
                        <Zap className="w-8 h-8 text-accent-main-000" />
                        <div className="card-title text-2xl font-bold text-text-100">
                          Press Release Writing
                        </div>
                      </div>
                      <div
                        className="card-badge px-3 py-1 rounded-full text-sm font-medium"
                        style={{
                          backgroundColor: "hsl(var(--accent-main-000)/0.1)",
                          color: "hsl(var(--accent-main-000))",
                        }}
                      >
                        보도자료 작성
                      </div>
                    </div>

                    <div className="card-description text-text-300 mb-4">
                      전문적인 보도자료를 자동으로 작성하는 AI 서비스
                    </div>

                    <ul className="card-features space-y-2 text-sm">
                      <li className="flex items-start">
                        <span className="text-accent-main-000 mr-2">✓</span>
                        <span className="text-text-200">
                          업계 표준 보도자료 형식
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-accent-main-000 mr-2">✓</span>
                        <span className="text-text-200">
                          헤드라인 자동 생성
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-accent-main-000 mr-2">✓</span>
                        <span className="text-text-200">
                          인용문 및 배경 정보 구성
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-accent-main-000 mr-2">✓</span>
                        <span className="text-text-200">
                          SEO 최적화 키워드 삽입
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-accent-main-000 mr-2">✓</span>
                        <span className="text-text-200">
                          다국어 번역 지원
                        </span>
                      </li>
                    </ul>

                    <div className="mt-6 text-center">
                      <button
                        className="bg-accent-main-000 hover:bg-accent-main-100 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
                        onClick={() => handleEngineSelect("press-release-writing")}
                      >
                        시작하기
                      </button>
                    </div>
                  </div>

                  {/* Digital News Writing Card */}
                  <div
                    className="product-card rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2 p-6 border-2 border-transparent hover:border-purple-600"
                    style={{ 
                      backgroundColor: "hsl(var(--bg-000))",
                      background: "linear-gradient(135deg, hsl(var(--bg-000)) 0%, rgba(147, 51, 234, 0.05) 100%)"
                    }}
                    onClick={() => handleEngineSelect("digital-news-writing")}
                  >
                    <div className="card-header flex justify-between items-start mb-4">
                      <div className="flex items-center gap-3">
                        <Crown className="w-8 h-8 text-purple-600" />
                        <div className="card-title text-2xl font-bold text-text-100">
                          Digital News Writing
                        </div>
                      </div>
                      <div
                        className="card-badge px-3 py-1 rounded-full text-sm font-medium"
                        style={{
                          backgroundColor: "rgba(147, 51, 234, 0.1)",
                          color: "rgb(147, 51, 234)",
                        }}
                      >
                        디지털 뉴스 작성
                      </div>
                    </div>

                    <div className="card-description text-text-300 mb-4">
                      디지털 플랫폼에 최적화된 뉴스 콘텐츠 작성 서비스
                    </div>

                    <ul className="card-features space-y-2 text-sm">
                      <li className="flex items-start">
                        <span className="text-purple-600 mr-2">✓</span>
                        <span className="text-text-200">
                          모바일 최적화 콘텐츠
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-purple-600 mr-2">✓</span>
                        <span className="text-text-200">
                          소셜미디어 연동 콘텐츠
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-purple-600 mr-2">✓</span>
                        <span className="text-text-200">
                          멀티미디어 요소 통합
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-purple-600 mr-2">✓</span>
                        <span className="text-text-200">
                          실시간 트렌드 반영
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-purple-600 mr-2">✓</span>
                        <span className="text-text-200">
                          인터랙티브 콘텐츠 생성
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-purple-600 mr-2">✓</span>
                        <span className="text-text-200">
                          분석 대시보드 제공
                        </span>
                      </li>
                    </ul>

                    <div className="mt-6 text-center">
                      <button
                        className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
                        onClick={() => handleEngineSelect("digital-news-writing")}
                      >
                        시작하기
                      </button>
                    </div>
                  </div>
                </div>

                {/* Recommendation Text */}
                <div className="text-center text-text-300 text-sm">
                  💡 일일 뉴스 분석은{" "}
                  <strong className="text-blue-500">Daily News Analysis</strong>를 |
                  보도자료 작성은{" "}
                  <strong className="text-accent-main-000">Press Release Writing</strong>을 |
                  디지털 뉴스 작성은{" "}
                  <strong className="text-purple-600">Digital News Writing</strong>을 선택하세요
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LandingPage;
